import { useCallback, useEffect, useRef, useState } from 'react'
import type React from 'react'
import { useReducedMotion } from 'framer-motion'
import { useMediaQuery } from '../../hooks/useMediaQuery'

const AUTOPLAY_MS = 6000

export function usePeekInside(count: number, autoplay: boolean) {
  const [active, setActive] = useState(0)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [paused, setPaused] = useState(false)
  const reduceMotion = useReducedMotion() ?? false
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const trackRef = useRef<HTMLDivElement>(null)

  const scrollTrackTo = useCallback(
    (index: number, behavior: ScrollBehavior) => {
      const track = trackRef.current
      const child = track?.children[index] as HTMLElement | undefined
      if (!track || !child) return
      track.scrollTo({
        left: child.offsetLeft - (track.clientWidth - child.clientWidth) / 2,
        behavior: reduceMotion ? 'auto' : behavior,
      })
    },
    [reduceMotion],
  )

  const goTo = useCallback(
    (index: number) => {
      const next = (index + count) % count
      setActive(next)
      if (!isDesktop) scrollTrackTo(next, 'smooth')
    },
    [count, isDesktop, scrollTrackTo],
  )

  const prev = useCallback(() => goTo(active - 1), [active, goTo])
  const next = useCallback(() => goTo(active + 1), [active, goTo])

  // Mobile: the scroll position is the source of truth for the active slide.
  const onTrackScroll = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    const center = track.scrollLeft + track.clientWidth / 2
    let best = 0
    let bestDist = Infinity
    Array.from(track.children).forEach((el, i) => {
      const node = el as HTMLElement
      const dist = Math.abs(node.offsetLeft + node.clientWidth / 2 - center)
      if (dist < bestDist) {
        bestDist = dist
        best = i
      }
    })
    setActive(best)
  }, [])

  // Keep the mobile track aligned when switching from desktop to mobile.
  useEffect(() => {
    if (!isDesktop) scrollTrackTo(active, 'auto')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop])

  useEffect(() => {
    if (!autoplay || reduceMotion || paused || lightboxIndex !== null) return
    const id = window.setTimeout(() => goTo(active + 1), AUTOPLAY_MS)
    return () => window.clearTimeout(id)
  }, [autoplay, reduceMotion, paused, lightboxIndex, active, goTo])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      prev()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      next()
    }
  }

  const openLightbox = (index: number) => {
    goTo(index)
    setLightboxIndex(index)
  }

  const changeLightbox = (index: number) => {
    setLightboxIndex(index)
    goTo(index)
  }

  return {
    active,
    isDesktop,
    reduceMotion,
    trackRef,
    goTo,
    prev,
    next,
    onTrackScroll,
    onKeyDown,
    lightboxIndex,
    openLightbox,
    changeLightbox,
    closeLightbox: () => setLightboxIndex(null),
    pauseHandlers: {
      onMouseEnter: () => setPaused(true),
      onMouseLeave: () => setPaused(false),
      onTouchStart: () => setPaused(true),
      onFocus: () => setPaused(true),
      onBlur: (e: React.FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPaused(false)
      },
    },
  }
}
